import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { DateFormat } from 'constants/General'
import { RewardsDataPoint } from './types'

dayjs.extend(advancedFormat)

const DAYS_IN_THREE_YEARS = 1096
const DAYS_IN_YEAR = 365

export const formatEndOfMonth = () => {
  return dayjs().endOf('month').format(DateFormat.MMM_D)
}

export function calculateCompoundRewards(initialAmount: number, apy: number) {
  let cumulativeRewards = 0
  const dailyReward = calculateCompoundedRewardsForSingleDay(initialAmount, apy)

  return Array.from({ length: DAYS_IN_THREE_YEARS }, (_, day) => {
    if (day > 0) {
      cumulativeRewards += dailyReward
    }

    const tooltipLabel = generateTooltipLabelBasedOnDays(day)
    const totalValue = Math.round(initialAmount + cumulativeRewards)

    return {
      day: day.toString(),
      rewards: totalValue,
      tooltipLabel,
    }
  })
}

export function calculateCompoundedRewardsForSingleDay(initialAmount: number, apy: number) {
  const decimalAnnualRate = apy / 100
  const dailyReward = (initialAmount * decimalAnnualRate) / DAYS_IN_YEAR

  return Math.round(dailyReward * 100) / 100
}

export function generateTooltipLabelBasedOnDays(day: number): string {
  if (day === 0) {
    return 'Today'
  }

  if (day === 365) {
    return '1 year'
  }

  if (day === 730) {
    return '2 years'
  }

  if (day === 1095) {
    return '3 years'
  }

  if (day < 30) {
    return `${day} ${day === 1 ? 'day' : 'days'}`
  }

  if (day < 365) {
    const months = Math.floor(day / 30)
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }

  const years = Math.floor(day / 365)
  const remainingDays = day % 365
  const months = Math.floor(remainingDays / 30)

  if (months === 0) {
    return `${years} ${years === 1 ? 'year' : 'years'}`
  }

  return `${years} ${years === 1 ? 'year' : 'years'} and ${months} ${months === 1 ? 'month' : 'months'}`
}

export function calculateChartTicks(data: RewardsDataPoint[]) {
  const maxValue = Math.max(...data.map(d => d.rewards))
  const minValue = Math.min(...data.map(d => d.rewards))
  const dataRange = maxValue - minValue
  const padding = dataRange * 0.1
  const paddedMin = minValue - padding
  const paddedMax = maxValue + padding

  // Determine formatting scale
  let scale = 1
  if (paddedMax >= 1000000) {
    scale = 1000000
  } else if (paddedMax >= 1000) {
    scale = 1000
  }

  // Convert to display units
  const displayMin = paddedMin / scale
  const displayMax = paddedMax / scale
  const displayRange = displayMax - displayMin

  // Calculate nice step size (prefer whole numbers like 1, 2, 5, 10, 20, 50, 100, etc)
  const minTickCount = 6
  const roughStep = displayRange / (minTickCount - 1)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const normalizedStep = roughStep / magnitude
  let niceStep = 10
  if (normalizedStep <= 1) {
    niceStep = 1
  } else if (normalizedStep <= 2) {
    niceStep = 2
  } else if (normalizedStep <= 5) {
    niceStep = 5
  }
  const displayStep = niceStep * magnitude

  // Round min/max to nice boundaries
  const roundedMin = Math.floor(displayMin / displayStep) * displayStep
  const roundedMax = Math.ceil(displayMax / displayStep) * displayStep

  // Generate evenly-spaced ticks (ensure at least minTickCount)
  const tickCount = Math.max(minTickCount, Math.round((roundedMax - roundedMin) / displayStep) + 1)
  const axisTicks = Array.from({ length: tickCount }, (_, i) => {
    const tickValue = (roundedMin + i * displayStep) * scale
    // Round to avoid floating-point precision issues (e.g., 1150.0000000000002 -> 1150)
    return Math.round(tickValue * 100) / 100
  })

  return {
    tickCount,
    axisTicks,
  }
}

export const formatAPY = (rate: number) => `${formatAmount(rate)}%`

export const formatAmount = (amount: number) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const isValidAmount = (value: string) => {
  if (!value || typeof value !== 'string') return false

  const cleanedValue = value.replaceAll(',', '').trim()
  const amountPattern = /^(?!0$)\d+(\.\d+)?$/

  if (!amountPattern.test(cleanedValue)) return false

  const numValue = Number.parseFloat(cleanedValue)
  return numValue > 0
}
